import { useEffect, useState } from "react";
import { components } from "../components";
import { hooks } from "../hooks";
import visaCardImg from "../assets/icons/visa.png";
import masterCardImg from "../assets/icons/master.png";
import close from "../assets/icons/close.png";
import apiService from "../service/ApiService";
import _ from "lodash";
import { setPaymentMethod } from "../store/slices/appSlice";
import constants from "../helpers/constants";
import { Routes } from "../routes";
import { AddPaymentMethod } from "./AddPaymentMethod";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { showErrorToast } from "../components/Toast";

interface PaymentMethod {
  paymentMethodId: string;
  brand: "VISA" | "MASTERCARD";
  lastFourDigits: string;
  expiryMonth: string;
  expiryYear: string;
}

export const PaymentMethods: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  const location = hooks.useLocation();
  const isDisable = location?.state?.isDisable || false;
  const [opacity, setOpacity] = useState<number>(0);
  const [paymentRequest, setPaymentRequest] = useState<any>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [showSheet, setShowSheet] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const selectedSiteId = useSelector((state: RootState) => state.selectedSiteReducer.selectedSiteId);
  const paymentMethod = useSelector((state: RootState) => state.appReducer.paymentMethod);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor("#F6F9F9", "#F6F9F9", dispatch);

  useEffect(() => {
    getPaymentMethodList();
  }, []);

  useEffect(() => {
    if (paymentMethod) {
      setSelectedMethodId(paymentMethod.paymentMethodId)
    }
  }, [paymentMethod]);

  const getPaymentMethodList = async () => {
    const { data } = await apiService.getPaymentMethodListAsync();
    if (data) {
      setPaymentMethods(data);
    }
    setLoading(false);
  };

  const createPaymentRequest = async () => {
    setShowSheet(true);
    const { data, error } = await apiService.createPaymentRequestAsync(
      selectedSiteId
    );
    if (data) {
      setPaymentRequest(data);
    } else if (error) {
      showErrorToast(error || constants.defaultErrorMessage);
    }
    setLoading(false);
  };

  const onAddPaymentMethod = () => {
    if (paymentMethods.length >= constants.maxPaymentMethods) {
      console.error(
        `Sorry, a maximum of ${constants.maxPaymentMethods} payment methods allowed to be attached to your account.`
      );
    } else {
      createPaymentRequest();
    }
  };

  const handleDelete = async (paymentMethodId: string) => {
    setLoading(true);
    const data = await apiService.deletePaymentMethodAsync(paymentMethodId);
    if (data) {
      const filteredPaymentMethods = _.filter(
        paymentMethods,
        (item) => item.paymentMethodId !== paymentMethodId
      );
      setPaymentMethods(filteredPaymentMethods);
      dispatch(
        setPaymentMethod(
          _.isEmpty(filteredPaymentMethods)
            ? {}
            : {
              brand: filteredPaymentMethods[0].brand,
              lastFourDigits: filteredPaymentMethods[0].lastFourDigits,
              paymentMethodId: filteredPaymentMethods[0].paymentMethodId,
            }
        )
      );
    }
    setLoading(false);
  };

  const onSelectPayment = (payment: any) => {
    dispatch(
      setPaymentMethod({
        brand: payment.brand,
        lastFourDigits: payment.lastFourDigits,
        paymentMethodId: payment.paymentMethodId,
        payType: "Card",
      })
    );
    setSelectedMethodId(payment.paymentMethodId);
    navigate(Routes.Checkout);
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header title="Payment Methods" showGoBack={true} />;
  };

  const renderHeaderContent = (): JSX.Element => {
    return (
      <div
        style={{
          marginLeft: 20,
          marginTop: 10,
        }}
      >
        <h3 style={{ fontWeight: 600 }}>Payment Methods </h3>
        <h5 style={{ fontWeight: 200 }}>
          We accept major all debit and credit cards.
        </h5>
        <div
          style={{
            alignItems: "center",
            display: "flex",
            marginTop: 10,
            gap: 10,
          }}
        >
          <img
            src={visaCardImg}
            alt="visa"
            style={{
              width: 60,
              fill: "#fff",
              height: 40,
              objectFit: "contain",
            }}
          />
          <img
            src={masterCardImg}
            alt="master"
            style={{
              width: 60,
              fill: "#fff",
              height: 40,
              objectFit: "contain",
            }}
          />
        </div>
      </div>
    );
  };

  const renderPaymentMethods = (): JSX.Element => {
    return (
      <main className="scrollable container">
        <section style={{ paddingTop: 10 }}>
          {paymentMethods.map((method) => (
            <div
              key={method.paymentMethodId}
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--white-color)",
                borderRadius: 10,
                border: method.paymentMethodId === selectedMethodId
                  ? "2px solid var(--main-turquoise)"
                  : "none",
                padding: "15px 20px",
                marginBottom: 15,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginTop: 5,
              }}
              onClick={
                isDisable && isDisable
                  ? () => onSelectPayment(method)
                  : undefined
              }
            >
              <img
                src={method.brand === "VISA" ? visaCardImg : masterCardImg}
                alt={method.brand}
                style={{
                  width: 40,
                  height: 25,
                  objectFit: "contain",
                  marginRight: 15,
                }}
              />

              <div
                style={{
                  marginRight: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <span
                  className="t12"
                  style={{ fontWeight: 500, fontSize: "1rem" }}
                >
                  **** **** **** {method.lastFourDigits}
                </span>
                <span
                  className="t12"
                  style={{ fontSize: "0.8rem", }}
                >
                  Expires {method.expiryMonth} / {method.expiryYear}
                </span>
              </div>
              {isDisable && isDisable ? null : (
                <button
                  onClick={() => handleDelete(method.paymentMethodId)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={close}
                    alt="Delete"
                    style={{ width: 14, height: 14 }}
                  />
                </button>
              )}
            </div>
          ))}
        </section>
      </main >
    );
  };

  const renderAddPaymentButton = (): JSX.Element => {
    return (
      <div
        style={{
          display: "flex",
          paddingLeft: 20,
          paddingRight: 20,
          marginBottom: 20,
          justifyContent: "center",
          alignContent: "center",
        }}
      >
        <components.Button
          text="+ Add Payment Method"
          onClick={onAddPaymentMethod}
        />
      </div>
    );
  };

  const renderLoader = (): JSX.Element | null => {
    if (isLoading) {
      return <components.Loader />;
    }
    return null;
  };

  const onSavePayment = () => {
    getPaymentMethodList();
  };

  return (
    <div id="screen" style={{ opacity }}>
      {renderHeader()}
      {renderHeaderContent()}
      {isLoading ? (
        renderLoader()
      ) : (
        <>
          {renderPaymentMethods()}
          {renderAddPaymentButton()}
        </>
      )}
      {showSheet && paymentRequest ? (
        <components.BottomSheet
          maxHeight="80vh"
          open={showSheet}
          onClose={() => setShowSheet(false)}
          title="Add Payment Method"
          closeButton={true}
        >
          {paymentRequest && (
            <AddPaymentMethod
              paymentRequest={paymentRequest}
              onClose={() => setShowSheet(false)}
              onAddPayment={onSavePayment}
            />
          )}
        </components.BottomSheet>
      ) : null}
    </div>
  );
};
